import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FolderKanban, MessageSquare, Coins, Bell, ArrowRight, TrendingUp, Plus, Sparkles, Smartphone } from "lucide-react";
import { useApp } from "@/app/AppLayout";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import api from "@/lib/api";

function Card({ children, className = "", ...p }) {
  return <div className={`bg-white rounded-2xl border border-[#E5E7EB] ${className}`} {...p}>{children}</div>;
}

export default function Dashboard() {
  const { user } = useAuth();
  const { credits } = useApp();
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [rec, setRec] = useState(null);
  const [usage, setUsage] = useState(null);
  const [messages, setMessages] = useState([]);
  const [apk, setApk] = useState(null);

  useEffect(() => {
    api.get("/projects").then((r) => setProjects(r.data)).catch(() => {});
    api.get("/billing/recommendation").then((r) => setRec(r.data)).catch(() => {});
    api.get("/billing/usage").then((r) => setUsage(r.data)).catch(() => {});
    api.get("/messages").then((r) => setMessages(r.data.messages.slice(0, 3))).catch(() => {});
    api.get("/content").then((r) => setApk(r.data)).catch(() => {});
  }, []);

  return (
    <div className="max-w-6xl mx-auto px-6 py-8 animate-fade-up">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <p className="text-xs font-mono uppercase tracking-[0.2em] text-muted2">Espace de recherche</p>
          <h1 className="font-serif text-3xl sm:text-4xl font-bold text-navy mt-1">Bonjour {user?.name?.split(" ")[0]}</h1>
          <p className="text-muted2 mt-1">Votre recherche continue ici.</p>
        </div>
        <Button onClick={() => navigate("/app/projects?new=1")} className="bg-navy text-white hover:bg-navy-800 rounded-full gap-2" data-testid="dash-new-project"><Plus className="h-4 w-4" /> Nouveau projet</Button>
      </div>
      {apk?.apk_url && (
        <a href={apk.apk_url} target="_blank" rel="noreferrer" data-testid="dash-apk-btn" className="mt-4 flex items-center gap-3 rounded-2xl border border-lisungi-yellow/40 bg-lisungi-yellow/10 px-5 py-3 hover:bg-lisungi-yellow/20 transition-colors">
          <Smartphone className="h-5 w-5 text-navy" />
          <span className="text-sm font-semibold text-navy">{apk.apk_button_label || "Télécharger Lisungi AI pour Android"}</span>
          <ArrowRight className="h-4 w-4 text-navy ml-auto" />
        </a>
      )}

      {rec?.show && (
        <div className="mt-6 bg-navy text-white rounded-2xl p-5 flex items-center gap-4" data-testid="upgrade-banner">
          <TrendingUp className="h-6 w-6 text-lisungi-yellow shrink-0" />
          <p className="text-sm flex-1">{rec.message}</p>
          <Button onClick={() => navigate("/app/billing")} className="bg-lisungi-yellow text-navy hover:bg-lisungi-yellow/90 rounded-full font-semibold shrink-0">Passer à Premium</Button>
        </div>
      )}

      <div className="grid md:grid-cols-3 gap-4 mt-6">
        <Card className="p-5">
          <div className="flex items-center gap-2 text-muted2 text-sm"><Coins className="h-4 w-4 text-lisungi-yellow" /> Crédits</div>
          <p className="font-serif text-4xl font-bold text-navy mt-2">{credits}</p>
          <p className="text-xs text-muted2">disponibles ce mois</p>
          {usage && <Progress value={100 - (usage.used_pct || 0)} className="h-1.5 mt-3" />}
        </Card>
        <Card className="p-5">
          <div className="flex items-center gap-2 text-muted2 text-sm"><FolderKanban className="h-4 w-4 text-lisungi-yellow" /> Projets</div>
          <p className="font-serif text-4xl font-bold text-navy mt-2">{projects.length}</p>
          <p className="text-xs text-muted2">projets de recherche</p>
        </Card>
        <Card className="p-5">
          <div className="flex items-center gap-2 text-muted2 text-sm"><Bell className="h-4 w-4 text-lisungi-yellow" /> Messages</div>
          <p className="font-serif text-4xl font-bold text-navy mt-2">{messages.filter((m) => !m.read).length}</p>
          <p className="text-xs text-muted2">non lus</p>
        </Card>
      </div>

      <div className="grid lg:grid-cols-3 gap-5 mt-5">
        <Card className="lg:col-span-2 p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-serif text-xl font-bold text-navy">Vos projets</h2>
            <button onClick={() => navigate("/app/projects")} className="text-sm text-navy font-medium hover:text-lisungi-yellow flex items-center gap-1">Tout voir <ArrowRight className="h-3.5 w-3.5" /></button>
          </div>
          {projects.length === 0 ? (
            <div className="text-center py-12 border-2 border-dashed border-[#E5E7EB] rounded-xl">
              <FolderKanban className="h-8 w-8 text-muted2 mx-auto" />
              <p className="text-muted2 mt-3 text-sm">Aucun projet pour l'instant.</p>
              <Button onClick={() => navigate("/app/projects?new=1")} className="mt-4 bg-navy text-white hover:bg-navy-800 rounded-full">Créer mon premier projet</Button>
            </div>
          ) : (
            <div className="space-y-3">
              {projects.slice(0, 4).map((p) => (
                <button key={p.id} onClick={() => navigate(`/app/projects/${p.id}`)} className="w-full text-left p-4 rounded-xl border border-[#E5E7EB] hover:border-navy hover:shadow-sm transition-all" data-testid={`dash-project-${p.id}`}>
                  <div className="flex items-center justify-between">
                    <div className="min-w-0">
                      <span className="text-[10px] font-mono uppercase tracking-wide bg-navy/5 text-navy px-2 py-0.5 rounded">{p.type}</span>
                      <p className="font-semibold text-navy mt-1.5 truncate">{p.title}</p>
                      <p className="text-xs text-muted2 truncate">{p.subject || "Sujet non défini"}</p>
                    </div>
                    <div className="text-right shrink-0 ml-4">
                      <p className="text-sm font-semibold text-navy">{p.progress}%</p>
                      <Progress value={p.progress} className="h-1.5 w-20 mt-1" />
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </Card>

        <Card className="p-6">
          <h2 className="font-serif text-xl font-bold text-navy mb-4">Prochaine étape</h2>
          <div className="space-y-3">
            <button onClick={() => navigate("/app/chat")} className="w-full flex items-center gap-3 p-3 rounded-xl bg-lisungi-bg hover:bg-navy hover:text-white group transition-all text-left">
              <MessageSquare className="h-5 w-5 text-lisungi-yellow" /><span className="text-sm font-medium">Dialoguer avec Lisungi</span>
            </button>
            <button onClick={() => navigate("/app/search")} className="w-full flex items-center gap-3 p-3 rounded-xl bg-lisungi-bg hover:bg-navy hover:text-white transition-all text-left">
              <Sparkles className="h-5 w-5 text-lisungi-yellow" /><span className="text-sm font-medium">Rechercher des publications</span>
            </button>
          </div>
          <div className="mt-6 pt-5 border-t border-[#E5E7EB]">
            <p className="text-xs font-mono uppercase tracking-wide text-muted2 mb-3">Messages récents</p>
            {messages.length === 0 ? <p className="text-sm text-muted2">Aucun message.</p> :
              messages.map((m) => (
                <div key={m.id} className="mb-2 text-sm">
                  <p className="font-medium text-navy truncate">{m.title}</p>
                  <p className="text-xs text-muted2 truncate">{m.content}</p>
                </div>
              ))}
          </div>
        </Card>
      </div>
    </div>
  );
}
